import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { offlineDB } from '../services/offlineDB';
import api from '../services/api';

interface OfflineContextType {
  isOnline: boolean;
  syncStatus: 'idle' | 'syncing' | 'error';
  pendingChanges: number;
  syncChanges: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error'>('idle');
  const [pendingChanges, setPendingChanges] = useState(0);

  const updatePendingChanges = async () => {
    try {
      const queue = await offlineDB.getQueue();
      setPendingChanges(queue.length);
    } catch (error) {
      console.error('[Offline] Error updating pending changes:', error);
    }
  };

  const syncChanges = async () => {
    if (!isOnline || syncStatus === 'syncing') {
      console.log('🔄 [Offline] Sync skipped - isOnline:', isOnline, 'syncStatus:', syncStatus);
      return;
    }

    setSyncStatus('syncing');
    console.log('🔄 [Offline] Starting sync...');

    try {
      const queue = await offlineDB.getQueue();
      console.log(`🔄 [Offline] Queue size: ${queue.length}`);
      
      for (const change of queue) {
        try {
          console.log('🔄 [Offline] Syncing change:', change);
          
          // Items erstellen
          if (change.type === 'CREATE' && change.entity === 'item') {
            console.log('🔄 [Offline] Creating item via API...');
            const response = await api.post('/items', change.data);
            const realItem = response.data.data;
            console.log('✅ [Offline] Item created successfully:', realItem.id);
            
            // Notify UI to replace temp item with real item
            if (change.tempItemId) {
              window.dispatchEvent(new CustomEvent('offline-item-synced', {
                detail: { 
                  tempId: change.tempItemId, 
                  realItem: realItem 
                }
              }));
              console.log('📤 [Offline] Dispatched offline-item-synced event:', change.tempItemId, '→', realItem.id);
            }
          }
          // Items aktualisieren
          else if (change.type === 'UPDATE' && change.entity === 'item') {
            await api.patch(`/items/${change.data.id}`, change.data);
          }
          // Items löschen
          else if (change.type === 'DELETE' && change.entity === 'item') {
            await api.delete(`/items/${change.data.id}`);
          }
          
          // Nach erfolgreichem Sync aus Queue entfernen
          console.log('🗑️ [Offline] Removing from queue:', change.id);
          await offlineDB.removeFromQueue(change.id);
          console.log('✅ [Offline] Removed from queue:', change.id);
        } catch (syncError: any) {
          console.error('❌ [Offline] Failed to sync change:', change.id, syncError?.message);
          // Bei Fehler: Change bleibt in Queue für nächsten Versuch
        }
      }

      console.log('✅ [Offline] All changes processed, updating pending count...');
      setSyncStatus('idle');
      
      // Queue neu laden und State aktualisieren
      const newQueue = await offlineDB.getQueue();
      const newCount = newQueue.length;
      console.log('✅ [Offline] New queue size:', newCount);
      setPendingChanges(newCount);
      
      console.log('✅ [Offline] Sync completed, pending count updated to:', newCount);
      
      // Event für UI-Refresh nach Sync
      window.dispatchEvent(new CustomEvent('offline-sync-complete'));
    } catch (error: any) {
      console.error('❌ [Offline] Sync error:', error?.message);
      setSyncStatus('error');
    }
  };

  useEffect(() => {
    // Online/Offline Event Listener
    const handleOnline = () => {
      console.log('🌐 Online');
      setIsOnline(true);
      // Sync direkt starten
      setTimeout(() => syncChanges(), 500);
    };

    const handleOffline = () => {
      console.log('📴 Offline');
      setIsOnline(false);
    };

    // Socket reconnect listener
    const handleSocketReconnect = () => {
      console.log('🔌 [Offline] Socket reconnected, triggering sync...');
      if (isOnline || navigator.onLine) {
        setTimeout(() => syncChanges(), 500);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('socket-reconnected', handleSocketReconnect);

    // Service Worker registrieren
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', {
        updateViaCache: 'none' // sw.js nie cachen, damit Updates sofort erkannt werden
      }).then((registration) => {
        console.log('[App] Service Worker registered:', registration);

        // Auf Updates prüfen
        registration.addEventListener('updatefound', () => {
          console.log('[App] Service Worker update found');
        });
      }).catch((error) => {
        console.error('[App] Service Worker registration failed:', error);
      });
    }

    // IndexedDB initialisieren
    offlineDB.init().then(() => {
      console.log('[App] IndexedDB initialized');
      updatePendingChanges();
    });

    // Regelmäßig Sync-Status prüfen
    const interval = setInterval(() => {
      if (isOnline) {
        updatePendingChanges();
      }
    }, 10000); // Alle 10 Sekunden

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('socket-reconnected', handleSocketReconnect);
      clearInterval(interval);
    };
  }, []);

  return (
    <OfflineContext.Provider value={{ isOnline, syncStatus, pendingChanges, syncChanges }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
}
